/**
 * CMS SDK - useCategories Hook
 * 分类管理 Hook
 */

import { useState, useCallback, useEffect } from 'react';
import { useCMS } from '../context/CMSContext';

/**
 * 分类管理 Hook
 * @param {boolean} autoLoad - 是否自动加载
 * @returns {Object} 分类操作对象
 */
export function useCategories(autoLoad = true) {
  const { api } = useCMS();
  const [categories, setCategories] = useState([]);
  const [categoryTree, setCategoryTree] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * 加载分类列表
   */
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getCategories();
      const items = result.items || result.data || result || [];
      setCategories(items);
      return items;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api]);

  /**
   * 加载分类树
   */
  const loadTree = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getCategoryTree();
      setCategoryTree(result);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api]);

  /**
   * 创建分类
   */
  const create = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.createCategory(data);
      setCategories(prev => [...prev, result]);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api]);

  /**
   * 更新分类
   */
  const update = useCallback(async (categoryId, data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.updateCategory(categoryId, data);
      setCategories(prev => prev.map(c => c.id === categoryId ? result : c));
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api]);

  /**
   * 删除分类
   */
  const remove = useCallback(async (categoryId) => {
    setLoading(true);
    setError(null);
    try {
      await api.deleteCategory(categoryId);
      setCategories(prev => prev.filter(c => c.id !== categoryId));
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api]);

  /**
   * 获取子分类
   */
  const getChildren = useCallback((parentId) => {
    return categories.filter(c => c.parent_id === parentId);
  }, [categories]);

  /**
   * 构建分类树
   */
  const buildTree = useCallback((parentId = null) => {
    const children = categories
      .filter(c => c.parent_id === parentId)
      .sort((a, b) => a.sort_order - b.sort_order);
    
    return children.map(category => ({
      ...category,
      children: buildTree(category.id),
    }));
  }, [categories]);

  // 自动加载
  useEffect(() => {
    if (autoLoad) {
      load();
    }
  }, [autoLoad, load]);

  return {
    categories,
    categoryTree: categoryTree.length ? categoryTree : buildTree(),
    loading,
    error,
    load,
    loadTree,
    create,
    update,
    remove,
    getChildren,
    buildTree,
  };
}

export default useCategories;
